import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  CircularProgress,
  Alert,
  Box,
  Typography,
} from '@mui/material';
import api from '../services/api';

interface MinMaxCalcProps {
  open: boolean;
  onClose: () => void;
  selectedItems: any[];
  onComplete: () => void;
}

interface MinMaxItem {
  store_id: number;
  store_name: string;
  manufacturer_id: number;
  manufacturer_name: string;
  medicine_name: string;
  pack_size: string;
  inventory_quantity: number;
  sales_total_sold_60d: number;
  min: number;
  max: number;
}

const MinMaxCalc: React.FC<MinMaxCalcProps> = ({ open, onClose, selectedItems, onComplete }: MinMaxCalcProps) => {
  const [items, setItems] = useState<MinMaxItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (open && selectedItems.length > 0) {
      initializeItems();
    }
  }, [open, selectedItems]);

  const initializeItems = () => {
    const initializedItems = selectedItems.map((item: any) => {
      const sales60d = item.sales_total_sold_60d || 0;
      const calculatedMin = Math.round((sales60d / 60) * 21);
      const calculatedMax = Math.round((sales60d / 60) * 35);

      return {
        store_id: item.store_id,
        store_name: item.store_name,
        manufacturer_id: item.manufacturer_id,
        manufacturer_name: item.manufacturer_name,
        medicine_name: item.medicine_name,
        pack_size: item.pack_size,
        inventory_quantity: item.inventory_quantity || 0,
        sales_total_sold_60d: sales60d,
        min: calculatedMin,
        max: calculatedMax,
      };
    });

    setItems(initializedItems);
  };

  const handleRecalculate = () => {
    initializeItems();
  };

  const handleMinChange = (index: number, value: string) => {
    const numValue = parseInt(value) || 0;
    setItems((prev: MinMaxItem[]) => {
      const newItems = [...prev];
      newItems[index] = { ...newItems[index], min: numValue };
      return newItems;
    });
  };

  const handleMaxChange = (index: number, value: string) => {
    const numValue = parseInt(value) || 0;
    setItems((prev: MinMaxItem[]) => {
      const newItems = [...prev];
      newItems[index] = { ...newItems[index], max: numValue };
      return newItems;
    });
  };

  const validateItems = (): string | null => {
    for (const item of items) {
      if (item.min > item.max) {
        return `Invalid min-max for ${item.medicine_name}: Min (${item.min}) cannot be greater than Max (${item.max})`;
      }
      if (item.min < 0 || item.max < 0) {
        return `Invalid min-max for ${item.medicine_name}: Values cannot be negative`;
      }
    }
    return null;
  };

  const handleSubmit = async () => {
    setError(null);
    
    const validationError = validateItems();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    try {
      const payload = items.map((item: MinMaxItem) => ({
        store_id: item.store_id,
        manufacturer_id: item.manufacturer_id,
        min: item.min,
        max: item.max,
      }));

      const response = await api.post('/utilities/item-min-max/bulk-upsert', { items: payload });

      if (response.data.success) {
        setSuccess(true);
        setTimeout(() => {
          onComplete();
          onClose();
        }, 1500);
      } else {
        setError('Failed to save min-max values');
      }
    } catch (err: any) {
      console.error('Error saving min-max:', err);
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to save min-max values');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Min-Max Calculator</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Successfully updated {items.length} items!
          </Alert>
        )}

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Formula: Min = (Sales 60d ÷ 60) × 21 days | Max = (Sales 60d ÷ 60) × 35 days
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Edit the values below as needed, then click Submit to save.
          </Typography>
        </Box>

        <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Store</TableCell>
                <TableCell>Manufacturer</TableCell>
                <TableCell>Medicine Name</TableCell>
                <TableCell>Pack Size</TableCell>
                <TableCell align="right">Current Stock</TableCell>
                <TableCell align="right">Sales 60d</TableCell>
                <TableCell align="right">Min</TableCell>
                <TableCell align="right">Max</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item: MinMaxItem, index: number) => (
                <TableRow key={`${item.store_id}_${item.manufacturer_id}`}>
                  <TableCell>{item.store_name}</TableCell>
                  <TableCell>{item.manufacturer_name}</TableCell>
                  <TableCell>{item.medicine_name}</TableCell>
                  <TableCell>{item.pack_size}</TableCell>
                  <TableCell align="right">{item.inventory_quantity}</TableCell>
                  <TableCell align="right">{item.sales_total_sold_60d}</TableCell>
                  <TableCell align="right">
                    <TextField
                      type="number"
                      value={item.min}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleMinChange(index, e.target.value)}
                      size="small"
                      sx={{ width: 80 }}
                      inputProps={{ min: 0 }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      type="number"
                      value={item.max}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleMaxChange(index, e.target.value)}
                      size="small"
                      sx={{ width: 80 }}
                      inputProps={{ min: 0 }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleRecalculate} disabled={saving}>
          Recalculate
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving || items.length === 0}
          startIcon={saving && <CircularProgress size={20} />}
        >
          {saving ? 'Saving...' : 'Submit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MinMaxCalc;

